package ai.wagemix.logic.share

import ai.wagemix.data.WMMixSyncEvent
import ai.wagemix.data.WMStatsSyncEvent
import ai.wagemix.data.WMUnauthenticatedException
import com.fsryan.foundation.net.client.FSAccessTokenFetchFailedException
import ai.wagemix.logic.isGlobal
import ai.wagemix.logic.mixes.storedStatsRequest
import ai.wagemix.logic.mixes.storedStatsSignature
import ai.wagemix.logic.retrieveStoredMix
import ai.wagemix.logic.session
import ai.wagemix.logic.share.WMShareMixPresenter.Action
import ai.wagemix.logic.share.WMShareMixPresenter.Event
import ai.wagemix.logic.share.WMShareMixPresenter.Prompt
import ai.wagemix.logic.WMNavigationRequest
import ai.wagemix.logic.WMRoutes
import ai.wagemix.net.models.evaluateSharedBadgeLevel
import ai.wagemix.net.models.fromJobDescription
import ai.wagemix.net.models.kxs
import ai.wagemix.net.models.kxsArea
import ai.wagemix.net.models.kxsIndustry
import ai.wagemix.net.models.kxsNone
import ai.wagemix.net.models.WMBadge
import ai.wagemix.net.models.WMSharedMixCreate
import ai.wagemix.net.models.WMSharedMixPurpose
import ai.wagemix.net.models.WMShareResult
import com.fsryan.foundation.logic.FSPresenter
import com.fsryan.foundation.logic.FSPresenter.Event.NullableValueWrapper
import com.fsryan.foundation.logic.FSPrompt
import com.fsryan.foundation.logic.NullableValueWrapper
import com.fsryan.foundation.logic.presenterInstanceId
import com.fsryan.foundation.logic.prompting.defaultHandlePromptResponse
import com.fsryan.foundation.logic.prompting.FSBasePromptingPresenter
import com.fsryan.foundation.logic.prompting.FSPromptingPresenter
import com.fsryan.foundation.logic.userAction
import com.fsryan.foundation.models.FSPropertyContainer
import com.fsryan.foundation.util.log.FSLog
import kotlin.jvm.JvmInline
import kotlin.time.Clock
import kotlin.time.Instant
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.emptyFlow
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onCompletion
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.onStart
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

enum class WMPayRangeProblem { LowAboveHigh, NotPositive }

/** A non-null wrapper so nullable flow values can be collected in the view scope. */
private class Optional<T>(val value: T?)

/**
 * The mix being shared. [hourly] is the mode the mix builder's chart was last left in, so the dialog starts in the same
 * mode the user has been reading the figures in.
 */
data class WMShareTarget(
    val mixId: String,
    val title: String,
    val fromJobDescription: Boolean,
    val areaId: Int?,
    val industryId: Int?,
    val hourly: Boolean,
)

/** The badge the current pay range would earn, with the range validation. */
data class WMBadgePreview(val badge: WMBadge?, val problem: WMPayRangeProblem?, val statsReady: Boolean)

/**
 * The share dialog: configure (pay range, badge preview, purpose), create the link, show the
 * preview image when the API has rendered it. Two outer states: configuring and link ready.
 */
interface WMShareMixPresenter<P: Prompt, out E: Event<P>, in A: Action>: FSPromptingPresenter<P, E, A> {

    interface Action: FSPromptingPresenter.Action {
        sealed interface Base: Action

        @JvmInline
        value class ComposedSuperAction internal constructor(val wrapped: FSPromptingPresenter.Action.Base): Base

        /** The form changed; [low]/[high] are `null` while a field is empty. */
        @ConsistentCopyVisibility
        data class EditForm internal constructor(val hourly: Boolean, val low: Double?, val high: Double?, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<EditForm> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class Create internal constructor(val hourly: Boolean, val low: Double?, val high: Double?, val purpose: WMSharedMixPurpose, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<Create> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        /** Back to the configure state (share another way). */
        @ConsistentCopyVisibility
        data class Reset internal constructor(override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<Reset> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        /** Render the created link's preview image again, after it failed or stalled. */
        @JvmInline
        value class RequestRegeneratePreview internal constructor(override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<RequestRegeneratePreview> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = RequestRegeneratePreview(properties)
        }

        interface Factory<P: Prompt, out A: Action>: FSPromptingPresenter.Action.Factory<P, A> {
            fun createEditForm(hourly: Boolean, low: Double?, high: Double?, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createCreate(hourly: Boolean, low: Double?, high: Double?, purpose: WMSharedMixPurpose, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createReset(extendedProperties: Map<String, Any> = emptyMap()): A
            fun createRequestRegeneratePreview(extendedProperties: Map<String, Any> = emptyMap()): A
        }

        companion object {
            fun <P: Prompt> baseFactory(): Factory<P, Base> = object: Factory<P, Base> {
                private val base = FSPromptingPresenter.Action.baseFactory<P>()
                override fun createUserRespondedToPrompt(response: FSPrompt.Response<P>, extendedProperties: Map<String, Any>): Base = ComposedSuperAction(base.createUserRespondedToPrompt(response, extendedProperties))
                override fun createEditForm(hourly: Boolean, low: Double?, high: Double?, extendedProperties: Map<String, Any>): Base = EditForm(hourly, low, high, extendedProperties)
                override fun createCreate(hourly: Boolean, low: Double?, high: Double?, purpose: WMSharedMixPurpose, extendedProperties: Map<String, Any>): Base = Create(hourly, low, high, purpose, extendedProperties)
                override fun createReset(extendedProperties: Map<String, Any>): Base = Reset(extendedProperties)
                override fun createRequestRegeneratePreview(extendedProperties: Map<String, Any>): Base = RequestRegeneratePreview(extendedProperties)
            }
        }
    }

    interface Event<out P: Prompt>: FSPromptingPresenter.Event<P> {
        val targetUpdate: WMShareTarget?
        val badgePreviewUpdate: WMBadgePreview?
        /** The created link, or a wrapped `null` back in the configure state. */
        val resultUpdate: NullableValueWrapper<WMShareResult?>?
        /** The rendered preview image, once the API has produced it. */
        val previewImageUpdate: String?
        /** Whether the created link's preview image is rendering, ready, or failed and may be regenerated. */
        val previewStateUpdate: WMSharePreviewState?
        val navigationUpdate: WMNavigationRequest?

        @ConsistentCopyVisibility
        data class Base<out P: Prompt> internal constructor(
            private val base: FSPromptingPresenter.Event.Base<P>,
            override val targetUpdate: WMShareTarget? = null,
            override val badgePreviewUpdate: WMBadgePreview? = null,
            override val resultUpdate: NullableValueWrapper<WMShareResult?>? = null,
            override val previewImageUpdate: String? = null,
            override val previewStateUpdate: WMSharePreviewState? = null,
            override val navigationUpdate: WMNavigationRequest? = null
        ): Event<P>, FSPropertyContainer<Base<P>>, FSPromptingPresenter.Event<P> by base {
            override val extendedProperties: Map<String, Any> get() = base.extendedProperties
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(base = base.replaceExtendedProperties(properties))
        }

        interface Factory<P: Prompt, out E: Event<P>>: FSPromptingPresenter.Event.Factory<P, E> {
            fun createTargetUpdate(target: WMShareTarget): E
            fun createBadgePreview(preview: WMBadgePreview): E
            fun createResultUpdate(result: WMShareResult?): E
            fun createPreviewImage(url: String): E
            fun createPreviewStateUpdate(state: WMSharePreviewState): E
            fun createNavigation(request: WMNavigationRequest): E
        }

        companion object {
            fun <P: Prompt> baseFactory(): Factory<P, Base<P>> = object: Factory<P, Base<P>> {
                private val base = FSPromptingPresenter.Event.baseFactory<P>()
                override fun createPromptChange(prompt: P, loading: Boolean?, extendedProperties: Map<String, Any>) = Base(base.createPromptChange(prompt, loading, extendedProperties))
                override fun createLoadingChange(loading: Boolean, extendedProperties: Map<String, Any>) = Base(base.createLoadingChange(loading, extendedProperties))
                override fun none() = Base(base.none())
                override fun createTargetUpdate(target: WMShareTarget) = Base(base.none(), targetUpdate = target)
                override fun createBadgePreview(preview: WMBadgePreview) = Base(base.none(), badgePreviewUpdate = preview)
                override fun createResultUpdate(result: WMShareResult?) = Base(base.none(), resultUpdate = NullableValueWrapper(result))
                override fun createPreviewImage(url: String) = Base(base.none(), previewImageUpdate = url)
                override fun createPreviewStateUpdate(state: WMSharePreviewState) = Base(base.none(), previewStateUpdate = state)
                override fun createNavigation(request: WMNavigationRequest) = Base(base.none(), navigationUpdate = request)
            }
        }
    }

    interface Prompt: FSPromptingPresenter.Prompt {
        sealed interface Base: Prompt

        @JvmInline
        value class ComposedSuperPrompt internal constructor(val wrapped: FSPromptingPresenter.Prompt.Base): Base

        @ConsistentCopyVisibility
        data class ShareFailed internal constructor(val message: String?): Base, FSPrompt.Displayed {
            override val forceInteraction = false
            override val messageOverride: String? = message
            override val titleOverride: String? = null
            enum class Selection { Dismiss, Retry }
            fun selectionOf(selected: Int): Selection? = Selection.entries.getOrNull(selected)
        }

        data object SessionExpired: Base, FSPrompt.Displayed {
            override val forceInteraction = true
            override val messageOverride: String? = null
            override val titleOverride: String? = null
            enum class Selection { Dismiss, SignIn }
            fun selectionOf(selected: Int): Selection? = Selection.entries.getOrNull(selected)
        }

        /** Asking for the preview image again failed; the link itself is fine. */
        @ConsistentCopyVisibility
        data class PreviewRegenerationFailed internal constructor(val message: String?): Base, FSPrompt.Displayed {
            override val forceInteraction = false
            override val messageOverride: String? = message
            override val titleOverride: String? = null
            enum class Selection { Dismiss }
            fun selectionOf(selected: Int): Selection? = Selection.entries.getOrNull(selected)
        }

        interface Factory<out P: Prompt>: FSPromptingPresenter.Prompt.Factory<P> {
            fun shareFailed(message: String?): P
            fun sessionExpired(): P
            fun previewRegenerationFailed(message: String?): P
        }

        companion object {
            fun baseFactory(): Factory<Base> = object: Factory<Base> {
                override fun createBase(wrapped: FSPrompt.Base): Base = ComposedSuperPrompt(FSPromptingPresenter.Prompt.baseFactory().createBase(wrapped))
                override fun shareFailed(message: String?): Base = ShareFailed(message)
                override fun sessionExpired(): Base = SessionExpired
                override fun previewRegenerationFailed(message: String?): Base = PreviewRegenerationFailed(message)
            }
        }
    }

    fun userEditedForm(hourly: Boolean, low: Double?, high: Double?, extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedCreate(hourly: Boolean, low: Double?, high: Double?, purpose: WMSharedMixPurpose, extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedReset(extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedRegeneratePreview(extendedProperties: Map<String, Any> = emptyMap())

    companion object
}

abstract class WMBaseShareMixPresenter<
    P: Prompt,
    E: Event<P>,
    A: Action,
    EF: Event.Factory<P, E>,
    AF: Action.Factory<P, A>,
    PF: Prompt.Factory<P>,
    G: WMShareMixDataGateway
>(
    protected val mixId: String,
    protected val dataGateway: G,
    eventFactory: EF,
    actionFactory: AF,
    promptFactory: PF,
    cancelViewScopeOnUnready: Boolean,
    instanceId: String,
    protected val currentYear: () -> Int = { Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).year },
    /** The time a pending preview is measured against, to tell when it has stalled. */
    protected val now: () -> Instant = { Clock.System.now() },
    handlePromptResponse: suspend (FSPrompt.Response<P>, suspend E.() -> Unit) -> Unit = { response, emit ->
        context(eventFactory, promptFactory, dataGateway) { defaultShareMixHandlePromptResponse(mixId, response, emit) }
    }
): FSBasePromptingPresenter<P, E, A, EF, AF, PF>(cancelViewScopeOnUnready, instanceId, eventFactory, actionFactory, promptFactory, handlePromptResponse),
    WMShareMixPresenter<P, E, A> {

    override fun userEditedForm(hourly: Boolean, low: Double?, high: Double?, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createEditForm(hourly, low, high, extendedProperties))
    override fun userRequestedCreate(hourly: Boolean, low: Double?, high: Double?, purpose: WMSharedMixPurpose, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createCreate(hourly, low, high, purpose, extendedProperties))
    override fun userRequestedReset(extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createReset(extendedProperties))
    override fun userRequestedRegeneratePreview(extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createRequestRegeneratePreview(extendedProperties))

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun registerSubscriptions() {
        // Every share is its own link: the dialog always starts on the form, and shows only the link this session
        // creates, which the sync writes to the user's context. Once a link exists, its public document is mirrored
        // into the global context so the rendered preview image appears when the API writes it.
        dataGateway.contextUpdateFlow
            .filter { it.shareResultChanged(mixId) }
            .map { it.after.shareResult(mixId) }
            .onStart { emit(null) }
            .onEach { eventFactory.createResultUpdate(it).emit() }
            .flatMapLatest { result ->
                when (result) {
                    null -> flowOf(Optional(null))
                    else -> dataGateway.contextUpdateFlow
                        .filter { it.isGlobal && it.sharedMixChanged(result.sharedMixId) }
                        .map { Optional(it.after.sharedMix(result.sharedMixId)) }
                        .onStart {
                            emit(Optional(dataGateway.retrieveGlobalContext().sharedMix(result.sharedMixId)))
                            dataGateway.observeSharedMix(result.sharedMixId, createPresenterContext())
                        }
                        .onCompletion { dataGateway.stopObservingSharedMix(result.sharedMixId) }
                }
            }
            .onEach { shared -> shared.value?.previewImageUrl?.let { eventFactory.createPreviewImage(it).emit() } }
            .launchInViewScope()

        // The created link's preview state, from the user's own record of the share; a stalled render turns into
        // Failed on its own, which offers Regenerate.
        dataGateway.contextUpdateFlow
            .filter { it.shareResultChanged(mixId) }
            .map { it.after.shareResult(mixId) }
            .onStart { emit(null) }
            .flatMapLatest { result ->
                when (result) {
                    null -> emptyFlow()
                    else -> dataGateway.ownShareFlow(result.sharedMixId).previewStates(now)
                }
            }
            .onEach { eventFactory.createPreviewStateUpdate(it).emit() }
            .launchInViewScope()

        dataGateway.mixSyncEventFlow
            .filter { it.presenterInstanceId() == instanceId }
            .onEach { event ->
                when (event) {
                    is WMMixSyncEvent.CreateShare.Initial -> eventFactory.createLoadingChange(true).emit()
                    is WMMixSyncEvent.CreateShare.Success -> {
                        logMixShared(event)
                        eventFactory.createLoadingChange(false).emit()
                    }
                    is WMMixSyncEvent.CreateShare.Failure -> {
                        val prompt = when (event.error) {
                            is WMUnauthenticatedException, is FSAccessTokenFetchFailedException -> promptFactory.sessionExpired()
                            else -> promptFactory.shareFailed(event.error.message)
                        }
                        eventFactory.createPromptChange(prompt, loading = false).emit()
                    }
                    is WMMixSyncEvent.RegenerateSharePreview.Initial -> {
                        eventFactory.createPreviewStateUpdate(WMSharePreviewState.Generating).emit()
                    }
                    is WMMixSyncEvent.RegenerateSharePreview.Failure -> {
                        // The request never reached the renderer: the preview is still the failed one.
                        eventFactory.createPreviewStateUpdate(WMSharePreviewState.Failed).emit()
                        val prompt = when (event.error) {
                            is WMUnauthenticatedException, is FSAccessTokenFetchFailedException -> promptFactory.sessionExpired()
                            else -> promptFactory.previewRegenerationFailed(event.error.message)
                        }
                        eventFactory.createPromptChange(prompt).emit()
                    }
                    else -> Unit
                }
            }
            .launchInViewScope()

        dataGateway.statsSyncEventFlow
            .filter { it.presenterInstanceId() == instanceId }
            .onEach { event ->
                when (event) {
                    is WMStatsSyncEvent.Stats.Success, is WMStatsSyncEvent.Stats.Failure -> eventFactory.createLoadingChange(false).emit()
                    else -> Unit
                }
            }
            .launchInViewScope()
    }

    override suspend fun sendInitialEvents(isFirstOnReadyCall: Boolean) {
        val uid = dataGateway.session()?.uid ?: return eventFactory.createPromptChange(promptFactory.sessionExpired()).emit()
        val mix = dataGateway.retrieveStoredMix(uid, mixId) ?: return eventFactory.createPromptChange(promptFactory.genericError()).emit()
        val hourly = dataGateway.retrieveChartPrefs()?.hourly == true
        val target = WMShareTarget(mix.id, mix.title, mix.fromJobDescription, mix.filters.areaId, mix.filters.industryId, hourly)
        eventFactory.createTargetUpdate(target).emit()
        val signature = mix.storedStatsSignature()
        if (signature != null && dataGateway.retrieveGlobalContext().stats(signature) == null) {
            eventFactory.createLoadingChange(true).emit()
            dataGateway.syncStats(createPresenterContext(), mix.storedStatsRequest())
        }
    }

    override fun extractBasePromptingPresenterAction(action: A): FSPromptingPresenter.Action.Base? = (action as? Action.ComposedSuperAction)?.wrapped

    @Suppress("UNCHECKED_CAST")
    override fun FSPrompt.Response<*>.extract(): FSPrompt.Response<P> = this as FSPrompt.Response<P>

    override fun handleNonBasePromptingPresenterAction(action: A) {
        when (action) {
            is Action.EditForm -> launch { eventFactory.createBadgePreview(preview(action.hourly, action.low, action.high)).emit() }
            is Action.Create -> launch { handleCreate(action) }
            is Action.Reset -> launch {
                dataGateway.deleteShareResult(dataGateway.session()?.uid, mixId)
                eventFactory.createResultUpdate(null).emit()
            }
            is Action.RequestRegeneratePreview -> launch {
                val uid = dataGateway.session()?.uid ?: return@launch eventFactory.createPromptChange(promptFactory.sessionExpired()).emit()
                val result = dataGateway.retrieveUserContext(uid).shareResult(mixId) ?: return@launch
                dataGateway.regenerateSharePreview(uid, result.sharedMixId, createPresenterContext(action))
            }
            else -> logUnknownActionType(action)
        }
    }

    private suspend fun handleCreate(action: Action.Create) {
        val problem = payRangeProblem(action.low, action.high)
        if (problem != null) {
            return eventFactory.createBadgePreview(preview(action.hourly, action.low, action.high)).emit()
        }
        val uid = dataGateway.session()?.uid ?: return eventFactory.createPromptChange(promptFactory.sessionExpired()).emit()
        val mix = dataGateway.retrieveStoredMix(uid, mixId) ?: return eventFactory.createPromptChange(promptFactory.genericError()).emit()
        val areaId = mix.filters.areaId
        val industryId = mix.filters.industryId
        val filters = when {
            areaId != null -> WMSharedMixCreate.Filters.kxsArea(areaId)
            industryId != null -> WMSharedMixCreate.Filters.kxsIndustry(industryId)
            else -> WMSharedMixCreate.Filters.kxsNone()
        }
        val payRange = if (action.low != null && action.high != null) {
            WMSharedMixCreate.PayRange.kxs(high = action.high.toFloat(), low = action.low.toFloat())
        } else {
            null
        }
        dataGateway.createShare(
            uid,
            WMSharedMixCreate.kxs(filters = filters, hourly = action.hourly, mixId = mixId, payrange = payRange, purpose = action.purpose),
            createPresenterContext(action)
        )
    }

    /** `mix_shared` for the link just created, described by the form values its action carried. */
    private suspend fun logMixShared(event: WMMixSyncEvent.CreateShare.Success) {
        val action = event.userAction<Action.Create>()
        val hasPayRange = action?.low != null && action.high != null
        val badge = action?.let { preview(it.hourly, it.low, it.high).badge }
        FSLog.sendWMMixShared(mixId = event.mixId, hasPayRange = hasPayRange, badgeTier = badge)
    }

    private suspend fun preview(hourly: Boolean, low: Double?, high: Double?): WMBadgePreview {
        val problem = payRangeProblem(low, high)
        val uid = dataGateway.session()?.uid
        val mix = uid?.let { dataGateway.retrieveStoredMix(it, mixId) }
        val stats = mix?.storedStatsSignature()?.let { dataGateway.retrieveGlobalContext().stats(it) }
        val badge = if (problem == null && low != null && high != null && stats != null) {
            stats.evaluateSharedBadgeLevel(hourly, low.toFloat(), high.toFloat(), currentYear())
        } else {
            null
        }
        return WMBadgePreview(badge = badge, problem = problem, statsReady = stats != null)
    }

    protected open fun payRangeProblem(low: Double?, high: Double?): WMPayRangeProblem? = when {
        low == null && high == null -> null              // a range is optional
        low == null || high == null -> null              // half-typed: no verdict yet
        low <= 0 || high <= 0 -> WMPayRangeProblem.NotPositive
        low > high -> WMPayRangeProblem.LowAboveHigh
        else -> null
    }
}

context(eventFactory: EF, promptFactory: PF, dataGateway: G)
suspend fun <
    P: Prompt,
    E: Event<P>,
    EF: Event.Factory<P, E>,
    PF: Prompt.Factory<P>,
    G: WMShareMixDataGateway
> defaultShareMixHandlePromptResponse(mixId: String, response: FSPrompt.Response<P>, emit: suspend E.() -> Unit) {
    when (val prompt = response.prompt) {
        is Prompt.ShareFailed -> eventFactory.createPromptChange(promptFactory.none()).emit()   // Retry is the form's Create again
        is Prompt.PreviewRegenerationFailed -> eventFactory.createPromptChange(promptFactory.none()).emit()
        is Prompt.SessionExpired -> {
            eventFactory.createPromptChange(promptFactory.none()).emit()
            if (prompt.selectionOf(response.selected) == Prompt.SessionExpired.Selection.SignIn) {
                eventFactory.createNavigation(WMNavigationRequest.Route("${WMRoutes.LOGIN}?next=${WMRoutes.mix(mixId)}")).emit()
            }
        }
        else -> defaultHandlePromptResponse(response, emit)
    }
}

typealias WMDefaultShareMixPresenter = WMShareMixPresenter<Prompt.Base, Event.Base<Prompt.Base>, Action.Base>

fun WMShareMixPresenter.Companion.createDefault(
    mixId: String,
    dataGateway: WMShareMixDataGateway,
    instanceId: String = FSPresenter.createInstanceId("WMShareMixPresenter"),
    currentYear: () -> Int = { Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).year },
    now: () -> Instant = { Clock.System.now() }
): WMDefaultShareMixPresenter = object: WMBaseShareMixPresenter<
    Prompt.Base,
    Event.Base<Prompt.Base>,
    Action.Base,
    Event.Factory<Prompt.Base, Event.Base<Prompt.Base>>,
    Action.Factory<Prompt.Base, Action.Base>,
    Prompt.Factory<Prompt.Base>,
    WMShareMixDataGateway
>(
    mixId = mixId,
    dataGateway = dataGateway,
    eventFactory = Event.baseFactory(),
    actionFactory = Action.baseFactory(),
    promptFactory = Prompt.baseFactory(),
    cancelViewScopeOnUnready = true,
    instanceId = instanceId,
    currentYear = currentYear,
    now = now
) {}
